// How a bout ended, and how it went — read off the recorded bout, never recomputed.
//
// The result screen used to say two things: "{winner} wins!" and the engine's own `state.reason`
// string. `reason` is a mechanical sentence written for the log ("Sara Vance is stopped.", "More
// Stamina after 10 exchanges.") and it now lives in the Zone 3 Bout Recap, where a mechanical
// sentence belongs. This module supplies what the prominent display shows instead: a VERDICT, and a
// short account of the fight that earned it.
//
// PURE, AND OVER THE RECORD. Every function here takes a bout log record and returns data — no DOM,
// no card names, no globals. Card ids come back as ids and the UI resolves them, so this file can be
// tested against a fixture record with nothing else loaded.
//
// IT DOES NOT DECIDE ANYTHING THE ENGINE ALREADY DECIDED. `classifyBoutEnd` in engine.js already
// works out the cause, the loser, which event was decisive and which others contributed, and emits
// all of it on `bout.ended`. Re-deriving any of that here would be a second implementation of the
// rules for ending a bout — the exact failure the Bout Recap's superlatives were written to avoid
// (see boutSuperlatives in ui.js). This reads the event. The only thing it adds is a JUDGEMENT the
// engine has no reason to make: whether a stoppage reads as a knockout or an accumulation.
window.BoutReport = (function () {
  'use strict';

  var RANGES = ['Long', 'Pocket', 'Clinch'];

  // ---- WHERE KO ENDS AND TKO BEGINS -------------------------------------------------------------
  //
  // THE OBVIOUS TEST DOES NOT WORK. The decisive event is a `stamina.changed` with `before` and
  // `after`, and the tempting reading is "before - after was big, so it was a knockout". Stamina
  // clamps at zero, so that difference is always exactly what the loser had left: a 9-damage head
  // kick on a fighter with 2 Stamina records identically to a 2-damage jab. Measured over 500 bouts,
  // the distribution of `before - after` and of `before` are the same histogram to the unit.
  //
  // The unclamped number is on the `attack.resolved` event that caused it, which is why this bothers
  // to find that event rather than trusting the crossing.
  //
  // THE THRESHOLDS ARE MEASURED, NOT PICKED. Over 800 autoplayed bouts, 481 ended by impact, and the
  // loser was worn down in nearly all of them — mean 2.8 Stamina left, having already absorbed a
  // mean of 4.6 landed hits. A fresh fighter flattened by one shot barely exists in this game. So a
  // rule that called most stoppages a KO would be describing the common case with the rare word:
  //
  //     the finishing blow alone was worth 6+           20.4% of stoppages
  //     it overshot what was left by 3+                 21.4%
  //     6+ AND the loser still had 3+ to give           15.2%   <- this one
  //
  // The last is the only one that says something a player can feel: a big shot landed on somebody
  // who was still in the fight, rather than the tap that happened to be last. It fires on about one
  // bout in eleven, which is what keeps the word worth reading.
  var KO_DAMAGE = 6;
  var KO_STAMINA_LEFT = 3;

  function eventsOf(record) {
    var out = [];
    (record && record.exchanges || []).forEach(function (ex) {
      (ex.events || []).forEach(function (e) { out.push({ ex: ex.exchange, e: e }); });
    });
    return out;
  }

  function endEventOf(record) {
    var all = eventsOf(record);
    for (var i = all.length - 1; i >= 0; i--) {
      if (all[i].e.type === 'bout.ended') return all[i];
    }
    return null;
  }

  // The attack that caused the decisive crossing, for its UNCLAMPED damage.
  //
  // Matched by the card the crossing names. Falls back to the biggest landed hit on the loser that
  // exchange when the crossing carries no `sourceCardId` — which measured at 4.4% of stoppages, and
  // is a real case (a multi-strike attributes per strike) rather than a defensive nicety. Returns
  // null if nothing landed on the loser at all, and a null finishing blow is NOT a knockout: the
  // claim has to be provable from the record or it is not made.
  // IMPACT STOPPAGES ONLY. An exhaustion loss is the loser spending their own last Stamina on a cost
  // they could not pay — there is no finishing blow, and the winner may not have thrown anything that
  // exchange at all. Without this guard the fallback below found the biggest hit that happened to
  // land and the screen credited "EXHAUSTION — Sara Vance, Eight-Limb Barrage, Exchange 6", naming a
  // finisher for a fight nobody finished. A decision has the same problem and no loser at all.
  function finishingBlow(record, end) {
    if (!end || !end.e.loser || end.e.cause !== 'impact') return null;
    var ex = (record.exchanges || []).filter(function (x) { return x.exchange === end.ex; })[0];
    if (!ex) return null;
    var decisive = (ex.events || []).filter(function (e) { return e.id === end.e.decisiveEventId; })[0] || null;
    var landed = (ex.events || []).filter(function (e) {
      return e.type === 'attack.resolved' && e.defender === end.e.loser && (e.damage || 0) > 0;
    });
    if (!landed.length) return null;
    var byCard = decisive && decisive.sourceCardId
      ? landed.filter(function (e) { return e.cardId === decisive.sourceCardId; })[0]
      : null;
    var atk = byCard || landed.slice().sort(function (a, b) { return b.damage - a.damage; })[0];
    return {
      cardId: atk.cardId || null,
      damage: atk.damage || 0,
      level: atk.level || null,
      attacker: atk.fighter || null,
      staminaLeft: decisive ? decisive.before : null,
      exchange: end.ex
    };
  }

  // ---- THE VERDICT ------------------------------------------------------------------------------
  //
  // `verdict` is the word the screen shows. `cause` is the engine's own classification, kept
  // alongside it so a caller can tell WHY a verdict was reached without re-deriving it.
  function finishOf(record) {
    var end = endEventOf(record);
    var result = (record && record.result) || {};
    if (!end) {
      // A record with no bout.ended — a bout still running, or one composed before the event
      // existed. Say only what result already says; do not invent a finish.
      return {
        verdict: result.winner ? 'WIN' : 'DRAW', cause: null, winner: result.winner || null,
        loser: null, exchange: result.exchangeCount || 0, blow: null, decisionBasis: null,
        hitsAbsorbed: 0, reason: result.reason || ''
      };
    }
    var e = end.e;
    var blow = finishingBlow(record, end);
    var verdict;
    if (e.cause === 'decision') verdict = e.winner ? 'DECISION' : 'DRAW';
    else if (e.cause === 'doubleStoppage') verdict = e.mutualImpact ? 'DOUBLE KO' : 'DOUBLE STOPPAGE';
    else if (e.cause === 'attackCost' || e.cause === 'fatigue') verdict = 'EXHAUSTION';
    else if (blow && blow.damage >= KO_DAMAGE && blow.staminaLeft >= KO_STAMINA_LEFT) verdict = 'KO';
    else verdict = 'TKO';

    return {
      verdict: verdict,
      cause: e.cause || null,
      winner: e.winner || result.winner || null,
      loser: e.loser || null,
      exchange: end.ex,
      blow: blow,
      decisionBasis: e.decisionBasis || null,
      // How much the loser had already taken before the finish — the number that makes TKO mean
      // "worn down" rather than "unlucky". Counted across the whole bout, not just the last
      // exchange, which is all `contributingEventIds` covers.
      hitsAbsorbed: e.loser ? landedOn(record, e.loser) : 0,
      reason: result.reason || ''
    };
  }

  function landedOn(record, key) {
    var n = 0;
    eventsOf(record).forEach(function (r) {
      if (r.e.type === 'attack.resolved' && r.e.defender === key && (r.e.damage || 0) > 0) n++;
    });
    return n;
  }

  // ---- THE SHAPE OF THE FIGHT -------------------------------------------------------------------
  //
  // Facts only. Every one of these is countable off the record, and the sentences built from them in
  // summaryOf say nothing that is not here — a summation that reaches past its evidence is the thing
  // this codebase keeps having to take back out.
  function arcOf(record) {
    var exchanges = (record && record.exchanges) || [];
    var arc = {
      exchangeCount: exchanges.length,
      clash: { Long: 0, Pocket: 0, Clinch: 0 },
      dominantRange: null,
      dominantShare: 0,
      openedAt: null,
      closedAt: null,
      p1: sideStats(), p2: sideStats(),
      leadChanges: [],
      lastLeadTaken: null,     // { key, exchange } — who led at the end and from which exchange
      finalGap: 0
    };
    if (!exchanges.length) return arc;

    arc.openedAt = exchanges[0].range ? exchanges[0].range.clash : null;
    arc.closedAt = exchanges[exchanges.length - 1].range ? exchanges[exchanges.length - 1].range.clash : null;

    var lead = null;
    exchanges.forEach(function (ex) {
      var clash = ex.range && ex.range.clash;
      if (clash && arc.clash[clash] != null) arc.clash[clash]++;
      (ex.events || []).forEach(function (e) {
        if (e.type !== 'attack.resolved') return;
        var side = arc[e.fighter];
        if (!side) return;
        if ((e.damage || 0) > 0) {
          side.landed++;
          side.damage += e.damage;
          if (e.damage > side.biggest.damage) {
            side.biggest = { damage: e.damage, cardId: e.cardId || null, exchange: ex.exchange };
          }
        } else if (e.whiffed) side.whiffed++;
        if (e.clean) side.clean++;
      });
      // Who is ahead on Stamina after this exchange, and when that last changed hands. A tie is not
      // a lead: it holds whatever the previous state was rather than counting as a change, so two
      // fighters trading level do not manufacture a "lead change" every exchange.
      var s1 = ex.p1 && ex.p1.after ? ex.p1.after.stamina : 0;
      var s2 = ex.p2 && ex.p2.after ? ex.p2.after.stamina : 0;
      var now = s1 > s2 ? 'p1' : (s2 > s1 ? 'p2' : lead);
      if (now && now !== lead) {
        arc.leadChanges.push({ key: now, exchange: ex.exchange });
        lead = now;
      }
      arc.finalGap = Math.abs(s1 - s2);
    });

    RANGES.forEach(function (r) {
      if (arc.clash[r] > arc.dominantShare) { arc.dominantShare = arc.clash[r]; arc.dominantRange = r; }
    });
    arc.lastLeadTaken = arc.leadChanges.length ? arc.leadChanges[arc.leadChanges.length - 1] : null;
    return arc;
  }

  function sideStats() {
    return { landed: 0, damage: 0, clean: 0, whiffed: 0, biggest: { damage: 0, cardId: null, exchange: 0 } };
  }

  // ---- THE SUMMATION ----------------------------------------------------------------------------
  //
  // Two or three sentences: where it was fought, who carried it, and how the lead behaved. Each is
  // emitted only when the fact behind it is actually true of THIS bout — a fight that never settled
  // in one range gets no "settled in the Pocket" sentence, and one where the lead never changed gets
  // no turning point. A bout with nothing to say gets fewer sentences rather than a vaguer one.
  //
  // `names` is { p1, p2 }. Returns an array of plain strings; the caller escapes.
  //
  // DETERMINISTIC. The variant is chosen by hashing the bout rather than by Math.random, so the same
  // bout always reads the same way — a result screen that reworded itself on a re-render would be
  // unnerving, and a test could not pin it.
  function summaryOf(record, names) {
    var arc = arcOf(record);
    var fin = finishOf(record);
    if (!arc.exchangeCount) return [];
    var n = arc.exchangeCount;
    var pick = variantPicker(record);
    var out = [];
    var nameOf = function (k) { return (names && names[k]) || (k === 'p1' ? 'Fighter 1' : 'Fighter 2'); };

    // 1. WHERE IT HAPPENED. Only claimed when one band genuinely held the majority of the clashes;
    // "most of it happened somewhere" is not worth a sentence.
    if (arc.dominantRange && arc.dominantShare > n / 2) {
      var band = arc.dominantRange;
      if (arc.dominantShare === n) {
        out.push(pick([
          'Every exchange clashed in the ' + band + '. Neither fighter could move it.',
          'All ' + n + ' exchanges were fought in the ' + band + ', start to finish.'
        ], 'where'));
      } else {
        out.push(pick([
          arc.dominantShare + ' of the ' + n + ' exchanges clashed in the ' + band + '.',
          'The fight lived in the ' + band + ' — ' + arc.dominantShare + ' of ' + n + ' exchanges.',
          'It was a ' + band + ' fight: ' + arc.dominantShare + ' clashes of ' + n + ' there.'
        ], 'where'));
      }
    } else if (arc.openedAt && arc.closedAt && arc.openedAt !== arc.closedAt) {
      out.push(pick([
        'It opened in the ' + arc.openedAt + ' and finished in the ' + arc.closedAt + '.',
        'The range never settled — ' + arc.openedAt + ' at the first bell, ' + arc.closedAt + ' at the last.'
      ], 'where'));
    }

    // 2. WHO CARRIED IT. Volume, not the verdict — the verdict is already the headline.
    var a = arc.p1, b = arc.p2;
    var lead = a.landed > b.landed ? 'p1' : (b.landed > a.landed ? 'p2' : null);
    if (lead) {
      var hi = lead === 'p1' ? a : b, lo = lead === 'p1' ? b : a;
      out.push(pick([
        nameOf(lead) + ' landed ' + hi.landed + ' to ' + lo.landed + '.',
        nameOf(lead) + ' put ' + hi.landed + ' hits on the board against ' + lo.landed + '.',
        'On volume it was ' + nameOf(lead) + ' — ' + hi.landed + ' landed against ' + lo.landed + '.'
      ], 'volume'));
    } else if (a.landed) {
      out.push(pick([
        'Both fighters landed ' + a.landed + '. Nothing separated them on volume.',
        'Dead level on volume: ' + a.landed + ' landed each.'
      ], 'volume'));
    }

    // 3. HOW THE LEAD BEHAVED. The most interesting fact in most bouts, and the only one that
    // describes the fight as a sequence rather than a total. Skipped on a decision the lead never
    // changed hands in, where it would only restate the scoreline.
    var lt = arc.lastLeadTaken;
    // THE LEAD THAT CHANGED ON THE LAST EXCHANGE. "Took the lead in exchange 8 and held it" is
    // trivially true when the bout ended in exchange 8 — it held for no time at all. The fact worth
    // reporting there is the opposite one: whoever won was behind until the exchange that finished
    // it, which is the most dramatic shape a bout has and the phrasing above throws away.
    if (lt && arc.leadChanges.length > 1 && lt.exchange === n) {
      var trailed = arc.leadChanges[arc.leadChanges.length - 2].key;
      out.push(pick([
        nameOf(trailed) + ' led on Stamina until the exchange that ended it.',
        nameOf(lt.key) + ' was behind on Stamina right up to the final exchange.',
        'It stayed ' + nameOf(trailed) + "'s fight until the last exchange turned it."
      ], 'lead'));
    } else if (lt && arc.leadChanges.length > 1 && lt.exchange > 1) {
      var swaps = arc.leadChanges.length - 1;
      out.push(pick([
        nameOf(lt.key) + ' took the Stamina lead in exchange ' + lt.exchange + ' and held it.',
        'The lead changed hands ' + (swaps === 1 ? 'once' : swaps + ' times') + ' before ' +
          nameOf(lt.key) + ' took it for good in exchange ' + lt.exchange + '.',
        'It turned in exchange ' + lt.exchange + ', when ' + nameOf(lt.key) + ' went ahead to stay.'
      ], 'lead'));
    } else if (fin.verdict === 'DECISION' && arc.finalGap > 0) {
      out.push(pick([
        'It went the full ' + n + ' exchanges with ' + arc.finalGap + ' Stamina between them at the end.',
        'Ten exchanges, and ' + arc.finalGap + ' Stamina separated them when it stopped.'
      ], 'lead'));
    }
    return out;
  }

  // ---- RANGE DISCIPLINE: THE ONE NUMBER THAT SEPARATES SKILL LEVELS ---------------------------
  //
  // WHY THIS STAT AND NOT ANOTHER. Measured over ~33,000 seeded bouts across two independent
  // harnesses (2026-08-29 engagement review), every per-exchange statistic is the SAME whether
  // both sides are thinking or both sides are clicking at random: mean margin swing 2.27 vs 2.27,
  // exchanges moving the margin 3+ 39.2% vs 39.4%, comebacks and bout length likewise. Outcomes
  // cannot teach, because outcomes do not know who was paying attention.
  //
  // One statistic does separate them, and by a lot: the share of your attacks that were Optimal
  // for the range the exchange actually clashed at. ~44-53% under random play, ~83-92% under the
  // trained agent. That ~40-point spread is the widest legible skill signal in the game, the
  // engine computes it every exchange, the logger has always recorded it, and until now nothing
  // showed it to anybody.
  //
  // IMPROVISED STRIKES ARE EXCLUDED, and that is the whole judgement in this function. §7's
  // fallback is Optimal at Pocket BY CONSTRUCTION -- a fighter holding no legal Attack throws it
  // and scores a free Optimal. Counting those would let the one situation where the player had no
  // card decision at all inflate the number whose entire job is to measure card decisions. A
  // fighter reduced to improvising repeatedly would read as disciplined.
  //
  // NOTHING ELSE IS EXCLUDED. Whiff and Compromised both count against, which is the point. An
  // illegal-pair fallback (`wasLegal: false`) still counts: being forced into it IS a decision
  // quality event, arrived at by the cards you kept several exchanges earlier.
  //
  // Returns { optimal, total, rate } with rate null when total is 0 -- a bout in which a fighter
  // threw nothing has no discipline to report, and 0/0 must not render as 0%.
  function rangeDiscipline(record, side) {
    var optimal = 0, total = 0;
    ((record && record.exchanges) || []).forEach(function (ex) {
      var r = ex && ex[side] && ex[side].resolution;
      if (!r || !r.effectiveness) return;
      if (r.improvisedStrike) return;
      total++;
      if (r.effectiveness === 'Optimal') optimal++;
    });
    return { optimal: optimal, total: total, rate: total ? optimal / total : null };
  }

  // A stable variant chooser. Seeded from facts that identify THIS bout and cannot drift between
  // renders — names, when it started, how long it ran, who won. Each call site passes its own `salt`
  // so the three sentences do not all land on the same index of their own list.
  function variantPicker(record) {
    var f = (record && record.fighters) || {};
    var r = (record && record.result) || {};
    var base = [(f.p1 && f.p1.name) || '', (f.p2 && f.p2.name) || '', record && record.startedAt,
                (record && record.exchanges || []).length, r.winner || ''].join('|');
    return function (variants, salt) {
      var s = base + '#' + salt;
      var h = 2166136261;
      for (var i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = (h * 16777619) >>> 0; }
      return variants[h % variants.length];
    };
  }

  return { finishOf: finishOf, arcOf: arcOf, summaryOf: summaryOf, rangeDiscipline: rangeDiscipline,
           KO_DAMAGE: KO_DAMAGE, KO_STAMINA_LEFT: KO_STAMINA_LEFT };
})();
